import { StanCard } from '@/components';
import { FormHeader } from './FormHeader';
import { FormActions } from './FormActions';
import { StepRenderer } from './StepRenderer';
import { FlowFormHeader } from './FlowFormHeader';
import { AddAudienceModal } from '../audience/AddAudienceModal';
import { useStanRouter } from '@/hooks/useStanRouter';
import {
    useAddFlowForm,
    useAddFlowFormActions,
    useAddFlowFormValidation,
    useAudienceSelection,
    useFlowStepNavigation,
    useFlowSubmission,
} from '../../hooks';

export const AddFlowForm = () => {
    const { push } = useStanRouter();

    // Form state
    const { control, watch, setValue, handleSubmit, trigger, formState } = useAddFlowForm();
    const { errors } = formState;

    // Watch form values
    const flowName = watch('flowName');
    const selectedTrigger = watch('selectedTrigger');
    const selectedDate = watch('selectedDate');
    const selectedTime = watch('selectedTime');

    // Step navigation
    const {
        currentStep,
        stepTotal,
        submittedName,
        setSubmittedName,
        goToNextStep,
        goToPreviousStep,
        isFirstStep,
        isLastStep,
    } = useFlowStepNavigation(selectedTrigger);

    // Form submission
    const { submitFlow, isLoading } = useFlowSubmission();

    // Audience selection (for time-based flows)
    const { isModalOpen, selectedTags, recipientCount, openModal, closeModal, handleConfirm, handleDelete } =
        useAudienceSelection({ setValue });

    // Form validations
    const { canProceedToNextStep } = useAddFlowFormValidation({
        currentStep,
        selectedTrigger,
        selectedDate,
        selectedTime,
    });

    // Form actions
    const { handleBackOrCancel, handleNextOrCreate } = useAddFlowFormActions({
        currentStep,
        isFirstStep,
        isLastStep,
        flowName,
        goToNextStep,
        goToPreviousStep,
        setSubmittedName,
        handleSubmit,
        trigger,
        submitFlow: async data => {
            const result = await submitFlow(data);
            // Navigate to email creation after successful flow creation
            if (result.flow_id > 0) {
                push(`/flows/${result.flow_id}/new`);
            }
            return result;
        },
    });

    return (
        <div className='mx-auto flex w-full max-w-lg flex-col gap-10 md:px-4 md:pt-4'>
            <FlowFormHeader flowName={submittedName} />

            <StanCard>
                <FormHeader
                    currentStep={currentStep}
                    totalSteps={stepTotal}
                    isFirstStep={isFirstStep}
                    onBack={handleBackOrCancel}
                />

                <StepRenderer
                    currentStep={currentStep}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    watch={watch}
                    trigger={trigger}
                    selectedTrigger={selectedTrigger}
                    recipientCount={recipientCount}
                    hasSelectedTags={selectedTags.length > 0}
                    onOpenModal={openModal}
                    onDeleteAudience={handleDelete}
                />

                <FormActions
                    isLastStep={isLastStep}
                    isDisabled={!canProceedToNextStep}
                    isLoading={isLoading}
                    onAction={handleNextOrCreate}
                />
            </StanCard>

            <AddAudienceModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onConfirm={handleConfirm}
                initialTags={selectedTags}
            />
        </div>
    );
};
